// src/store/slices/plotPointsSlice.ts
/**
 * Plotted Points Redux Slice
 * 
 * Manages individual points plotted on the coordinate grid.
 * Phase 3: Points and Basic Shapes
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../types/redux';
import {
  PlottedPoint,
  PointStyle,
  PointSize,
  CoordinatePosition,
  Point2D,
} from '../../types/coordinateGrid';
import { calculateMidpoint } from '../../lib/coordinateGrid/shapeCalculations';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface PlotPointsState {
  points: PlottedPoint[];
  selectedPointIds: string[];
  nextId: number;
  /** Default styling for new points */
  defaultStyle: {
    style: PointStyle;
    size: PointSize;
    color: string;
    showCoordinates: boolean;
  };
  /** Auto-label mode (A, B, C... or P₁, P₂, P₃...) */
  labelMode: 'alpha' | 'numeric' | 'none';
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: PlotPointsState = {
  points: [],
  selectedPointIds: [],
  nextId: 1,
  defaultStyle: {
    style: 'dot',
    size: 'medium',
    color: '#3B82F6', // Blue
    showCoordinates: true,
  },
  labelMode: 'alpha',
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Generate next label based on label mode
 */
function generateLabel(index: number, mode: 'alpha' | 'numeric' | 'none'): string | undefined {
  if (mode === 'none') {
    return undefined;
  }

  if (mode === 'alpha') {
    // A, B, C, ..., Z, AA, AB, ...
    if (index < 26) {
      return String.fromCharCode(65 + index); // A-Z
    }
    const first = String.fromCharCode(65 + Math.floor((index - 26) / 26));
    const second = String.fromCharCode(65 + ((index - 26) % 26));
    return first + second;
  }

  // numeric mode: P₁, P₂, P₃, ...
  const subscript = String(index + 1)
    .split('')
    .map((d) => String.fromCharCode(0x2080 + parseInt(d)))
    .join('');
  return `P${subscript}`;
}

/**
 * Get next available label index
 */
function getNextLabelIndex(points: PlottedPoint[], mode: 'alpha' | 'numeric' | 'none'): number {
  if (mode === 'none') {
    return 0;
  }

  const existingLabels = new Set(points.map((p) => p.label).filter(Boolean));
  let index = 0;

  while (true) {
    const label = generateLabel(index, mode);
    if (!label || !existingLabels.has(label)) {
      return index;
    }
    index++;
  }
}

// =============================================================================
// REDUX SLICE
// =============================================================================

export const plotPointsSlice = createSlice({
  name: 'plotPoints',
  initialState,
  reducers: {
    /**
     * Add a new point to the grid
     */
    addPoint: (
      state,
      action: PayloadAction<{
        x: number;
        y: number;
        label?: string;
        style?: Partial<PlottedPoint>;
      }>
    ) => {
      const { x, y, label, style = {} } = action.payload;

      const labelIndex = getNextLabelIndex(state.points, state.labelMode);
      const autoLabel = label ?? generateLabel(labelIndex, state.labelMode);

      const newPoint: PlottedPoint = {
        id: `point-${state.nextId}`,
        x,
        y,
        label: autoLabel,
        showCoordinates: state.defaultStyle.showCoordinates,
        coordinatePosition: 'auto',
        style: state.defaultStyle.style,
        size: state.defaultStyle.size,
        color: state.defaultStyle.color,
        isSelected: false,
        ...style,
      };

      state.points.push(newPoint);
      state.nextId++;
    },

    /**
     * Update an existing point
     */
    updatePoint: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<PlottedPoint>;
      }>
    ) => {
      const point = state.points.find((p) => p.id === action.payload.id);
      if (point) {
        Object.assign(point, action.payload.updates);
      }
    },

    /**
     * Delete a point by ID
     */
    deletePoint: (state, action: PayloadAction<string>) => {
      state.points = state.points.filter((p) => p.id !== action.payload);
      state.selectedPointIds = state.selectedPointIds.filter((id) => id !== action.payload);
    },

    /**
     * Select a point (with optional multi-select via Ctrl key)
     */
    selectPoint: (
      state,
      action: PayloadAction<{
        id: string;
        ctrlKey?: boolean;
      }>
    ) => {
      const { id, ctrlKey = false } = action.payload;

      if (!ctrlKey) {
        // Deselect all points
        state.points.forEach((p) => {
          p.isSelected = false;
        });
        state.selectedPointIds = [];
      }

      const point = state.points.find((p) => p.id === id);
      if (point) {
        point.isSelected = !point.isSelected;
        if (point.isSelected) {
          state.selectedPointIds.push(id);
        } else {
          state.selectedPointIds = state.selectedPointIds.filter((pid) => pid !== id);
        }
      }
    },

    /**
     * Deselect all points
     */
    deselectAllPoints: (state) => {
      state.points.forEach((p) => {
        p.isSelected = false;
      });
      state.selectedPointIds = [];
    },

    /**
     * Delete all selected points
     */
    deleteSelectedPoints: (state) => {
      state.points = state.points.filter((p) => !p.isSelected);
      state.selectedPointIds = [];
    },

    /**
     * Set point style
     */
    setPointStyle: (
      state,
      action: PayloadAction<{
        id: string;
        style: PointStyle;
      }>
    ) => {
      const point = state.points.find((p) => p.id === action.payload.id);
      if (point) {
        point.style = action.payload.style;
      }
    },

    /**
     * Toggle coordinate display for a point
     */
    toggleCoordinateDisplay: (state, action: PayloadAction<string>) => {
      const point = state.points.find((p) => p.id === action.payload);
      if (point) {
        point.showCoordinates = !point.showCoordinates;
      }
    },

    /**
     * Calculate midpoint between two points and create a new point
     */
    calculateMidpoint: (
      state,
      action: PayloadAction<{
        pointId1: string;
        pointId2: string;
        createPoint?: boolean;
      }>
    ) => {
      const { pointId1, pointId2, createPoint = true } = action.payload;

      const point1 = state.points.find((p) => p.id === pointId1);
      const point2 = state.points.find((p) => p.id === pointId2);

      if (!point1 || !point2) {
        return;
      }

      const midpoint = calculateMidpoint(
        { x: point1.x, y: point1.y },
        { x: point2.x, y: point2.y }
      );

      if (createPoint) {
        const labelIndex = getNextLabelIndex(state.points, state.labelMode);
        const autoLabel = generateLabel(labelIndex, state.labelMode);

        const newPoint: PlottedPoint = {
          id: `point-${state.nextId}`,
          x: midpoint.x,
          y: midpoint.y,
          label: autoLabel,
          showCoordinates: state.defaultStyle.showCoordinates,
          coordinatePosition: 'auto',
          style: state.defaultStyle.style,
          size: state.defaultStyle.size,
          color: state.defaultStyle.color,
          isSelected: false,
        };

        state.points.push(newPoint);
        state.nextId++;
      }
    },

    /**
     * Set default point style
     */
    setDefaultStyle: (
      state,
      action: PayloadAction<Partial<PlotPointsState['defaultStyle']>>
    ) => {
      state.defaultStyle = {
        ...state.defaultStyle,
        ...action.payload,
      };
    },

    /**
     * Set label mode
     */
    setLabelMode: (state, action: PayloadAction<'alpha' | 'numeric' | 'none'>) => {
      state.labelMode = action.payload;
    },

    /**
     * Load points state (for scene loading)
     */
    loadPointsState: (state, action: PayloadAction<Partial<PlotPointsState>>) => {
      Object.assign(state, { ...initialState, ...action.payload });
    },

    /**
     * Clear all points
     */
    clearAllPoints: (state) => {
      state.points = [];
      state.selectedPointIds = [];
    },
  },
});

// Export actions
export const {
  addPoint,
  updatePoint,
  deletePoint,
  selectPoint,
  deselectAllPoints,
  deleteSelectedPoints,
  setPointStyle,
  toggleCoordinateDisplay,
  calculateMidpoint,
  setDefaultStyle,
  setLabelMode,
  loadPointsState,
  clearAllPoints,
} = plotPointsSlice.actions;

// Selectors
export const selectAllPoints = (state: RootState) => state.plotPoints.points;
export const selectSelectedPoints = (state: RootState) =>
  state.plotPoints.points.filter((p) => p.isSelected);
export const selectPointById = (id: string) => (state: RootState) =>
  state.plotPoints.points.find((p) => p.id === id);

export default plotPointsSlice.reducer;

